<template>
  <div id="privacy-page" class="content">
    <h4>Privacy Policy</h4>
    <p class="date">Last Updated: May 16, 2018</p>
    <p>
      OpenLikes respects the privacy of its users. This Privacy Policy (
      <strong>“Privacy Policy”</strong>) informs you about our online and offline information
      practices, the kinds of information we may collect, how we intend to use and share that
      information, and how you can opt-out of a use or correct or change such information.
    </p>
    <hr />
    <h5>About Us</h5>
    <p>
      We are OpenLikes Tech, Inc. (
      <strong>“OpenLikes”</strong>, <strong>“us”</strong>, <strong>“we”</strong> or
      <strong>“our”</strong>), a corporation registered under the laws of the state of Delaware in
      the United States with its principal office address at 660 4th Street #558, San Francisco, CA
      94107, USA.
    </p>
    <hr />
    <h5>About the OpenLikes Services</h5>
    <p>
      We provide users (
      <strong>“you”</strong>, <strong>“your”</strong>) with services, delivered via the OpenLikes
      website (currently located at
      <a href="http://www.openlikes.com">http://www.openlikes.com</a>), (the
      <strong>“Site”</strong>), the OpenLikes mobile applications, browser extensions, and/or any
      other platform or media through which we may make such services available from time to time,
      that allow you to explore the Internet, find new websites that may be of interest to you,
      receive recommendations of websites from OpenLikes, and join a community of individuals with
      similar tastes and interests (collectively, the <strong>“Services”</strong>).
    </p>
    <hr />
    <h5>About This Privacy Policy</h5>
    <p>
      This Privacy Policy informs you of OpenLikes’s policies and practices regarding the collection
      and use of information you submit to us or which we collect about you through our Services and
      informs you of options that you have to control or restrict the availability and use of your
      information. Wherever you reside or from whichever country you submit your information, you
      consent to our use of your information, consistent with this Privacy Policy, in the United
      States and in other territories where OpenLikes provides its Services. The above said does not
      apply to you if you are in the European Union (EU). Users in the EU should see the last
      section (“Additional Information for EU Residents”), which will inform you in detail about the
      personal data we process, how we process it, for which purposes we process it, with whom we
      share it, on what legal basis we process and transfer your personal data and which rights you
      have regarding the processing of your personal data.
    </p>
    <hr />
    <h5>Changes to This Privacy Policy</h5>
    <p>
      This Privacy Policy may change from time to time. You understand and agree that you will be
      deemed to have accepted the updated Privacy Policy if you use the Services after the updated
      Privacy Policy is posted on the Services. If at any point you do not agree to any portion of
      the Privacy Policy then in effect, you must immediately stop using the Services.
    </p>
    <hr />
    <p>
      <strong>Revision to the Privacy Policy.</strong> We may revise this Privacy Policy in our sole
      discretion, so please review it periodically. If you continue to visit this Site and use the
      Services made available to you after such changes have been made, you hereby provide your
      consent to the changes and such use indicates such consent.
    </p>
    <br />
    <p>
      <strong>Posting of Revised Privacy Policy.</strong> If there are any material changes to this
      Privacy Policy, OpenLikes will notify you by email or as otherwise required by applicable law.
      We will post any adjustments to the Privacy Policy on this web page, and the revised version
      will be effective immediately when it is posted (or upon notice as applicable). If you are
      concerned about how your information is used, bookmark this page and read this Privacy Policy
      periodically.
    </p>
    <br />
    <br />
    <p>
      <strong>New Uses of Personal Information.</strong> Additionally, before we use Personal
      Information for any new purpose not originally authorized by you, we will endeavor to provide
      information regarding the new purpose and give you the opportunity to opt-out. Where consent
      of the Individual for the Processing of Personal Information is otherwise required by law or
      contract, OpenLikes will endeavor to comply with the law or contract.
    </p>
    <hr />
    <h5>Your Account</h5>
    <p>
      You may choose to create a OpenLikes account or you may use the Services without creating an
      account. Either way, we consider you a
      <strong>“User”</strong> of the Services. You may also have created an account through one of
      OpenLikes’s connected social networking communities ( <strong>“Partner Communities”</strong>).
      Once you have created an account you may choose to add a photo or other information to your
      profile (the <strong>“OpenLikes Profile”</strong>).
    </p>
    <hr />
    <h5>Why Does OpenLikes Collect Information?</h5>
    <p>
      We want to give our Users the best possible experience, personalized for each User. We collect
      information to improve our Services, features and content, to administer your use of our
      Services, and to deliver content that we determine may be of interest to you.
    </p>
    <hr />
    <h5>Some Terms We Use in This Privacy Policy</h5>
    <p>
      <strong>“Agent”</strong> or <strong>“Processor”</strong> means any Third Party that Processes
      Personal Information pursuant to the instructions of, and solely for, OpenLikes or to which
      OpenLikes discloses Personal Information for use on its behalf.
    </p>
    <p>
      <strong>“Non-Identifying Information”</strong> means information that does not permit the
      identification of an individual person.
    </p>
    <p>
      <strong>“Log Data”</strong> is derived by our servers automatically as you engage with the
      Services, which record information that your browser or mobile device sends whenever you
      interact with the Service.
    </p>
    <p>
      <strong>“Personal Information”</strong> or <strong>“Personal Data”</strong> is any information
      relating to an identified or identifiable natural person ( <strong>“Individual”</strong>).
    </p>
    <p>
      <strong>“Process”</strong> or <strong>“Processing”</strong> means any operation which is
      performed upon Personal Information, whether or not by automatic means, such as collection,
      recording, organization, structuring, storage, adaptation or alteration, retrieval,
      consultation, use, disclosure by transmission, dissemination or otherwise making available,
      alignment or combination, restriction, erasure, or destruction.
    </p>
    <p>
      <strong>“Third Party”</strong> is any company, natural or legal person, public authority,
      agency, or body other than the Individual, OpenLikes or OpenLikes’s Agents.
    </p>
    <hr />
    <h5>What Information Does OpenLikes Collect?</h5>
    <p>
      OpenLikes will collect only that Personal Information which is relevant for the purposes of
      Processing that information as described in this Privacy Policy.
    </p>
    <p>
      <strong>Personal Information.</strong> When you create a OpenLikes account or connect through
      our Partner Communities, we will collect your e-mail address, gender, year of birth and
      specific or general location. If you register for our Services via a Partner Community, we
      will obtain certain Personal Information you have provided to the Partner Community (such as
      your name, profile picture, e-mail address, date of birth and specific address or general
      location). If you enable interoperability with a Third Party application, additional Personal
      Information that you have provided to that Third Party application may be collected and/or you
      may specifically authorize OpenLikes to access certain information from such Third Party
      application. The information we obtain may depend on the privacy settings you have with the
      Partner Community or Third Party application and the information obtained may not be visible
      in your account. If you personalize your OpenLikes account, we will also collect any
      information you provide, such as your full name, your photo or avatar and any optional
      information you decide to supply about yourself and your interests. We will first seek your
      specific permission before we gain any access to your contacts should you wish to enable
      sharing through the Services.
    </p>
    <p>
      <strong>Non-Identifying Information.</strong> We also collect some Non-Identifying Information
      about you and your interests as you navigate through and interact with our Services such as
      the URL of page(s) you view and the titles and content of the pages you save to OpenLikes. We
      may also receive Non-Identifying Information about you and your interests from other sources
      like Third Parties that add to their websites OpenLikes widgets or OpenLikes badges.
    </p>
    <p>
      <strong>Log Data.</strong> When you interact with the Services, our servers automatically
      record information that your browser or device sends. This Log Data may include information
      such as your computer’s IP address, browser type, and operating system, your mobile device
      identifier, the time of day of your access, your general physical location or your more
      specific location if you are connecting through your mobile device, and other analytics that
      help us deliver and monitor the effectiveness of our Services.
    </p>
    <p>
      <strong>Information You Provide.</strong> When you enter any other information into any
      postings or comments, we may collect this information.
    </p>
    <p>
      <strong>Contacting us.</strong> When you leave us feedback or write to us directly, we will
      collect any information that is contained in your feedback or comment form, which will include
      your e-mail address and other information that you may submit to us for reference or reply.
    </p>
    <hr />
    <h5>Does OpenLikes Use Cookies or Other Tracking Methods?</h5>
    <p>
      On the OpenLikes Site, OpenLikes and Third Parties that provide content, advertising, or other
      functionality on our Services may use cookies, pixel tags, local storage, and other
      technologies (
      <strong>“Technologies”</strong>) to automatically collect information through the Services. We
      use Technologies that are essentially small data files placed on your computer, tablet, mobile
      phone, or other devices (referred to collectively as a <strong>"device"</strong>) that allow
      us to record certain pieces of information whenever you visit or interact with our Sites,
      Services, applications, messaging, and tools, and to recognize you across devices.
    </p>
    <ul>
      <li>
        <strong>Cookies.</strong> Cookies are small text files placed in visitors’ computer browsers
        to store their preferences. Most browsers allow you to block and delete cookies. However, if
        you do that, the Site may not work properly.
      </li>
      <li>
        <strong>Pixel Tags/Web Beacons.</strong> A pixel tag (also known as a web beacon) is a piece
        of code embedded on the Site that collects information about users’ engagement on that web
        page. The use of a pixel allows us to record, for example, that a user has visited a
        particular web page or clicked on a particular advertisement.
      </li>
      <li>
        <strong>Social Media Widgets.</strong> Our Site includes social media features such as the
        Facebook “Like” button and LinkedIn (that might include widgets such as the share this
        button or other interactive mini-programs). These features may collect your IP address,
        which page you are visiting on our Site, and may set a cookie to enable the feature to
        function properly. These social media features are either hosted by a Third Party or hosted
        directly on our Site. Your interactions with these features are governed by the privacy
        policy of the company providing it.
      </li>
      <li>
        <strong>Analytics.</strong> We may also use Google Analytics and Google Analytics
        Demographics and Interest Reporting to collect information regarding visitor behavior and
        visitor demographics on some of our Services, and to develop website content. This analytics
        data is not tied to any Personal Information. For more information about Google Analytics,
        please visit
        <a href="http://www.google.com/policies/privacy/partners/"
          >http://www.google.com/policies/privacy/partners/</a
        >. You can opt out of Google’s collection and Processing of data generated by your use of
        the Services by going to
        <a href="http://tools.google.com/dlpage/gaoptout">http://tools.google.com/dlpage/gaoptout</a
        >.
      </li>
    </ul>
    <p>Our uses of such Technologies fall into the following general categories:</p>
    <ul>
      <li>
        <strong>Operationally Necessary.</strong> We may use Technologies that are necessary to the
        operation of our Site, Services, applications, and tools. This includes technologies that
        allow you access to our Site, Services, applications, and tools; that are required to
        identify irregular Site behavior, prevent fraudulent activity and improve security; or that
        allow you to make use of our functions such as shopping-carts, saved search, or similar
        functions;
      </li>
      <li>
        <strong>Performance Related.</strong> We may use cookies, web beacons, or other similar
        technologies to assess the performance of our Site, Services, applications, and tools,
        including as part of our analytic practices to help us understand how our visitors use our
        Sites, determine if you have interacted with our messaging, determine whether you have
        viewed an item or link, or to improve our Site content, Services, applications, or tools;
      </li>
      <li>
        <strong>Functionality Related.</strong> We may use cookies, web beacons, or other similar
        technologies that allow us to offer you enhanced functionality when accessing or using our
        Site, Services, applications, or tools. This may include identifying you when you sign into
        our Site or keeping track of your specified preferences, interests, or past items viewed so
        that we may enhance the presentation of content on our Site;
      </li>
      <li>
        <strong>Advertising or Targeting Related.</strong> We may use first-party or third-party
        cookies and web beacons to deliver content, including advertisements relevant to your
        interests, on our Site or on Third Party sites. This includes using technologies to
        understand the usefulness to you of the advertisements and content that has been delivered
        to you, such as whether you have clicked on an advertisement.
      </li>
    </ul>
    <p>
      <strong>Your Options:</strong> Most browsers are initially set up to accept cookies and
      tracking pixels. You can reset your browser to refuse all cookies or to indicate when a cookie
      is being set. Instructions to remove persistent cookies can be found through outside
      resources. Mobile devices may contain settings that allow you to disable tracking, analytics
      and/or location data. Be aware, however, that if you choose to disable cookies or opt out of
      tracking or disengage similar features on your mobile device, some areas of the Services may
      not be optimized for your use, you may not be able to access any or all of the functionality
      of the Services, and your experience using the Services will be limited.
    </p>
    <hr />
    <h5>How Can You Edit or Remove Your Personal or Other Information?</h5>
    <p>
      You may choose to edit the Personal Information that is in your OpenLikes account so that
      different or less information is available to be viewed. To remove other Personal Information
      that may not be visible in your account, you may contact us
      <a href="mailto:openlikes@expa.com">here</a> with a request that we delete your Personal
      Information from our database or remove other information that you no longer wish to include
      as part of the Services. We will use commercially reasonable efforts to honor your request.
    </p>
    <p>
      We may retain an archived copy of your records as required by law or for legitimate business
      purposes. When you receive messages from us, you may indicate a preference to stop receiving
      further communications from us and you will have the opportunity to “unsubscribe”. We may,
      however, still send you administrative messages regarding the Services. If a child between the
      ages of 13 and 18 wishes to erase or delete any information that he or she has included or
      shared via the Services, please contact us
      <a href="mailto:openlikes@expa.com">here</a> and we will promptly comply.
    </p>
    <hr />
    <h5>How Does OpenLikes Use Information Internally?</h5>
    <p>
      <strong>To Provide Products, Services, or Information Requested.</strong> OpenLikes may use
      information about you to fulfill requests for products, Services, or information, including
      information about potential or future Services, including to:
    </p>
    <ul>
      <li>Generally manage Individual information and accounts;</li>
      <li>Respond to questions, comments, and other requests;</li>
      <li>
        Provide access to certain areas, functionalities, and features of OpenLikes’s Services;
      </li>
      <li>Contact you to answer requests for customer support or technical support.</li>
    </ul>
    <p>
      <strong>Administrative Purposes.</strong> OpenLikes may use Personal Information about you for
      its administrative purposes, including to:
    </p>
    <ul>
      <li>Measure interest in OpenLikes’s Services;</li>
      <li>Develop new products and Services;</li>
      <li>Ensure internal quality control;</li>
      <li>Verify Individual identity;</li>
      <li>
        Communicate about Individual accounts and activities on OpenLikes’s Services and systems,
        and, in OpenLikes’s discretion, changes to any of our policies;
      </li>
      <li>
        Send email to the email address you provide to us to verify your account and for
        informational and operational purposes, such as account management, customer service, or
        system maintenance;
      </li>
      <li>Process payment for products or services purchased;</li>
      <li>Process applications and transactions;</li>
      <li>Prevent potentially prohibited or illegal activities;</li>
      <li>Enforce our Terms.</li>
    </ul>
    <p>
      <strong>Marketing.</strong> OpenLikes may use Personal Information to provide you with
      materials about offers, products, and Services that may be of interest, including new content
      or Services. OpenLikes may provide you with these materials by or email, as permitted by
      applicable law. Such uses include:
    </p>
    <ul>
      <li>To tailor content, advertisements, and offers;</li>
      <li>To notify you about offers, products, and services that may be of interest to you;</li>
      <li>To provide Services to you and our sponsors;</li>
      <li>
        For other purposes disclosed at the time that Individuals provide Personal Information; or
      </li>
      <li>Otherwise with your consent.</li>
      <li>
        You may contact us at any time to opt-out of the use of your Personal Information for
        marketing purposes, as further described in “How do I Opt Out?” section below.
      </li>
    </ul>
    <p>
      <strong>Research and Development.</strong> OpenLikes may use Personal Information to create
      non-identifiable information that we may use alone or in the aggregate with information
      obtained from other sources, in order to help us to optimally deliver our existing products
      and Services or develop new products and Services. From time to time, OpenLikes may perform
      research (online and offline) via surveys. We may engage Third-Party service providers to
      conduct such surveys on our behalf. All survey responses are voluntary, and the information
      collected will be used for research and reporting purposes to help us better serve Individuals
      by learning more about their needs and the quality of the products and services we provide.
      The survey responses may be utilized to determine the effectiveness of our Services, various
      types of communications, advertising campaigns, and/or promotional activities. If an
      Individual participates in a survey, the information given will be used along with that of
      other study participants. We may share anonymous Individual and aggregate data for research
      and analysis purposes.
    </p>
    <p>
      <strong>Services via Mobile Devices and Browser Extensions.</strong> From time to time,
      OpenLikes may provide Services that are specifically designed to be compatible and used on
      mobile devices. OpenLikes will collect certain information that your mobile device sends when
      you use such Services, like a device identifier, user settings, location information, mobile
      carrier, and the operating system of your device. Mobile versions of OpenLikes’s Services may
      require that users log in with an account. In such cases, information about use of mobile
      versions of the Services may be associated with accounts. In addition, OpenLikes may enable
      Individuals to download an application, widget, browser extension, or other tool that can be
      used on mobile or other computing devices. Some of these tools may store information on mobile
      or other devices. These tools may transmit Personal Information to OpenLikes to enable
      Individuals to access Accounts and to enable OpenLikes to track use of these tools. Some of
      these tools may enable users to email reports and other information from the tool. OpenLikes
      may use Personal Information or non-identifiable information transmitted to OpenLikes to
      enhance these tools, to develop new tools, for quality improvement and as otherwise described
      in this Privacy Policy or in other notices OpenLikes provides.
    </p>
    <p>
      <strong>Other Uses.</strong> OpenLikes may use Personal Information for which we have a
      legitimate interest, such as direct marketing, individual or market research, anti-fraud
      protection, or any other purpose disclosed to you at the time you provide Personal Information
      or with your consent.
    </p>
    <p>
      You agree that OpenLikes is free to use the content of any communications submitted by you via
      the Services, including any profile photo, submissions and collections of articles, for any
      purpose including developing, manufacturing, and/or marketing goods or Services. Some examples
      of these uses include posting to our community blog (community.openlikes.com) and social media
      such as Twitter and Facebook, subject to the applicable terms governing use of those social
      media services.
    </p>
    <hr />
    <h5>When Does OpenLikes Share Information with Others, and with Whom?</h5>
    <p>
      <strong>Sharing Via Your OpenLikes Account.</strong> The information provided in your
      OpenLikes account that you select to be publicly displayed will be public information and any
      User will be able to view it. You may edit or hide information in your profile at any time so
      that it is no longer accessible to the public, but even if you delete your data, it may
      continue to exist within or displayed via the Services and/or be shared by other Users. Also,
      when you enter any information (including Personal Information), into postings or comments via
      the Services, this information, may be available to some, a subgroup of, or even all Users of
      the Services, depending on how and where this information was posted by you and with whom you
      have elected to share the posting or comment.
    </p>
    <p>
      <strong>Aggregate Information &amp; Non-Identifying Information.</strong> We may share
      aggregated information that does not include Personal Information and we may otherwise
      disclose Non-Identifying Information and Log Data with Third Parties for industry analysis,
      demographic profiling, advertising, marketing and other purposes relating to our Services. Any
      aggregated information shared in these contexts will not contain your Personal Information.
    </p>
    <p>
      <strong>Service Providers.</strong> We may employ Third Party companies and individuals to
      facilitate our Services, to provide the Services on our behalf, to perform maintenance and
      research services, or to assist us in analyzing how our Services are used (such as
      infrastructure providers, database management, web or mobile analytics and improvement of the
      Services’ features).
    </p>
    <p>
      <strong>Business Transfers &amp; Affiliates.</strong> OpenLikes may share Personal Information
      with our business partners, and affiliates for our and our affiliates’ internal business
      purposes or to provide you with a product or service that you have requested. OpenLikes may
      also provide Personal Information to business partners with whom we may jointly offer products
      or services, or whose products or services we believe may be of interest to you. In such
      cases, our business partner’s name will appear, along with OpenLikes. OpenLikes requires our
      affiliates and business partners to agree in writing to maintain the confidentiality and
      security of Personal Information they maintain on our behalf and not to use it for any purpose
      other than the purpose for which OpenLikes provided them.
    </p>
    <p>
      If OpenLikes or the assets of OpenLikes are acquired by another company, or if OpenLikes is
      involved in financing due diligence processes or transition of service to another service
      provider, then we may transfer your information, including Personal Information, in connection
      with that acquisition or sale or transaction and OpenLikes will direct the recipient of this
      information to assume the rights and obligations described in this Privacy Policy.
    </p>
    <p>
      We also may share your information with affiliates in the same corporate family as OpenLikes.
    </p>
    <p>
      <strong>Marketing – Interest-Based Advertising and Third Party Marketing.</strong> Through our
      Services, we may allow Third-Party advertising partners to set tracking tools (e.g., cookies)
      to collect information regarding your activities (e.g., your IP address, page(s) visited, time
      of day). We may also share such de-identified information as well as selected Personal
      Information (such as demographic information) we have collected with Third-Party advertising
      partners. These advertising partners may use this information (and similar information
      collected from other websites) for purposes of delivering targeted advertisements to you when
      you visit non-OpenLikes related websites within their networks. This practice is commonly
      referred to as “interest-based advertising” or “online behavioral advertising. We may allow
      access to other data collected by the Site to facilitate transmittal of information that may
      be useful, relevant, valuable or otherwise of interest to you. If you prefer that we do not
      share your Personal Information with Third-Party advertising partners, you may opt-out of such
      sharing at no cost by following the instructions in the “How do I Opt Out?” below.
    </p>
    <p>
      <strong>Compliance with Laws &amp; Law Enforcement.</strong> We may access, preserve, and
      disclose your Personal Information, other account information, and content if we believe doing
      so is required or appropriate to: (i) comply with law enforcement or national security
      requests and legal process, such as a court order or subpoena; (ii) respond to your requests;
      (iii) protect your, our or others’ rights, property, or safety; (iv) to enforce OpenLikes
      policies or contracts; (v) when we believe disclosure is necessary or appropriate to prevent
      physical harm or financial loss or in connection with an investigation or prosecution of
      suspected or actual illegal activity; or (vi) if we, in good faith, believe that disclosure is
      otherwise necessary or advisable.
    </p>
    <hr />
    <h5>How Do I Opt Out?</h5>
    <p>
      <strong>General.</strong> You have the right to opt out of certain uses and disclosures of
      your Personal Information. Where you have consented to OpenLikes’s Processing of your Personal
      Information, you may withdraw that consent at any time and opt-out to further Processing by
      contacting us <a href="mailto:openlikes@expa.com">here</a>. Even if you opt-out, we may still
      collect and use non-Personal Information regarding your activities on our Sites and/or
      information from the advertisements on Third-Party websites for non-interest based advertising
      purposes, such as to determine the effectiveness of the advertisements.
    </p>
    <p>
      <strong>Email.</strong> If you receive an unwanted email from us, you can use the unsubscribe
      link found at the bottom of the email to opt-out of receiving future emails. We will process
      your request within a reasonable time after receipt. Note that you will continue to receive
      transaction-related emails regarding products or services you have requested. We may also send
      you certain non-promotional communications regarding OpenLikes and our Services and you will
      not be able to opt out of those communications (e.g., communications regarding updates to our
      Terms or this Privacy Policy).
    </p>
    <p>
      <strong>Mobile Devices.</strong> OpenLikes may occasionally send you push notifications
      through our mobile applications and other notices that may be of interest to you. You may at
      any time opt-out from receiving these types of communications by changing the settings on your
      mobile device. OpenLikes may also collect location-based information if you use our mobile
      applications. You may opt-out of this collection by changing the settings on your mobile
      device.
    </p>
    <p>
      <strong>“Do Not Track.”</strong> Do Not Track ( <strong>“DNT”</strong>) is a privacy
      preference that users can set in certain web browsers. DNT is a way for users to inform
      websites and services that they do not want certain information about their webpage visits
      collected over time and across websites or online services. Please note that we do not respond
      to or honor DNT signals or similar mechanisms transmitted by web browsers.
    </p>
    <p>
      <strong>Cookies and Interest-Based Advertising.</strong> As noted above, you may stop or
      restrict the placement of cookies on your computer or remove them from your browser by
      adjusting your web browser preferences. Please note that cookie-based opt-outs are not
      effective on mobile applications. However, on many mobile devices, application users may opt
      out of certain mobile ads via their device settings.
    </p>
    <p>
      The online advertising industry also provides websites from which you may opt-out of receiving
      targeted ads from our data partners and our other advertising partners that participate in
      self-regulatory programs. You can access these, and also learn more about targeted advertising
      and consumer choice and privacy, at
      <a href="http://www.networkadvertising.org/&zwnj;managing/&zwnj;opt_out.asp"
        >www.networkadvertising.org/&zwnj;managing/&zwnj;opt_out.asp</a
      >, or <a href="http://www.youronlinechoices.eu/">www.youronlinechoices.eu</a> and
      <a href="http://www.aboutads.info/&zwnj;choices/">www.aboutads.info/&zwnj;choices/</a>. You
      can also choose not to be included in Google Analytics
      <a href="mailto:openlikes@expa.com">here</a>.
    </p>
    <p>
      To be clear, whether you are using our opt-out or an online industry opt-out, these
      cookie-based opt-outs must be performed on each device and browser that you wish to have
      opted-out. For example, if you have opted-out on your computer browser, that opt-out will not
      be effective on your mobile device. You must separately opt-out on each device. Advertisements
      on Third-Party websites that contain the AdChoices link and that link to this Privacy Policy
      may have been directed to you based on anonymous, non-Personal Information collected by
      advertising partners over time and across websites. These advertisements provide a mechanism
      to opt-out of the advertising partners’ use of this information for interest-based advertising
      purposes.
    </p>
    <hr />
    <h5>How Can I Access, Rectify, Erase, and Restrict My Information?</h5>
    <p>
      You may inquire as to whether OpenLikes is Processing Personal Information about you, request
      access to Personal Information, and ask that we correct, amend or delete your Personal
      Information where it is inaccurate. Where otherwise permitted by applicable law, you may send
      an e-mail here or use any of the methods set out in this Privacy Policy to request access to,
      receive (port), seek rectification, or request erasure of Personal Information held about you
      by OpenLikes. Please include your full name, email address associated with your account, and a
      detailed description of your data request. Such requests will be processed in line with local
      laws.
    </p>
    <p>
      Although OpenLikes makes good faith efforts to provide Individuals with access to their
      Personal Information, there may be circumstances in which OpenLikes is unable to provide
      access, including but not limited to: where the information contains legal privilege, would
      compromise others’ privacy or other legitimate rights, where the burden or expense of
      providing access would be disproportionate to the risks to the Individual’s privacy in the
      case in question or where it is commercially proprietary. If OpenLikes determines that access
      should be restricted in any particular instance, we will provide you with an explanation of
      why that determination has been made and a contact point for any further inquiries. To protect
      your privacy, OpenLikes will take commercially reasonable steps to verify your identity before
      granting access to or making any changes to your Personal Information.
    </p>
    <hr />
    <h5>Where and How Long Will Information Be Collected and Stored?</h5>
    <p>
      OpenLikes is located in the United States. Accordingly, Personal Information we collect may be
      transferred to and stored in our servers or those of Third Parties that act on our behalf
      (such as hosting providers) in the United States or other countries outside of the European
      Economic Area. By using the Services you agree to such collection, transfer and storage
      outside of the European Economic Area or other territories where the information may have been
      collected.
    </p>
    <p>
      OpenLikes retains the Personal Information we receive as described in this Privacy Policy for
      as long as you use our Services or as necessary to fulfill the purpose(s) for which it was
      collected, provide our Services, resolve disputes, establish legal defenses, conduct audits,
      pursue legitimate business purposes, enforce our agreements, and comply with applicable laws.
    </p>
    <hr />
    <h5>How Secure Is the Information?</h5>
    <p>
      You should be aware that while we endeavor to take industry standard administrative, physical
      and technological measures to maintain the security of your Personal Information, we are not
      able to fully eliminate all security risks or potential breaches.
    </p>
    <p>
      By using the Site and Services or providing Personal Information to us, you agree that we may
      communicate with you electronically regarding security, privacy, and administrative issues
      relating to your use of the Site and Services. If we learn of a security system’s breach, we
      may attempt to notify you electronically by posting a notice on the Site or sending an e-mail
      to you. You may have a legal right to receive this notice in writing.
    </p>
    <hr />
    <h5>What Is the Policy Towards Children?</h5>
    <p>
      The Services are not directed to children under 13 (and in certain jurisdictions under the age
      of 16) years of age, and OpenLikes does not knowingly collect Personal Information from
      children under 13 (and in certain jurisdictions under the age of 16) years of age. OpenLikes
      urges parents to instruct their children never to give out their real names, addresses, or
      phone numbers, without parental permission, when online. If you learn that your child has
      provided us with Personal Information without your consent, you may alert us
      <a href="mailto:openlikes@expa.com">here</a>. If we learn that we have collected any Personal
      Information from children under 13 (and in certain jurisdictions under the age of 16), we will
      promptly take steps to delete such information and terminate the child’s Account.
    </p>
    <hr />
    <h5>California Privacy Rights</h5>
    <p>
      California law permits users who are California residents to request and obtain from us once a
      year, free of charge, a list of the Third Parties to whom we have disclosed their Personal
      Information (if any) for their direct marketing purposes in the prior calendar year, as well
      as the type of Personal Information disclosed to those parties.
    </p>
    <hr />
    <h5>What Policies Apply to Third Party Websites, Services and Applications?</h5>
    <p>
      The Services may contain links or integrations to Third Party applications, websites or
      services, and also may be accessed via Third Party applications, websites or services. Our
      display within the Services of links or referrals to other websites, services and applications
      does not signify our endorsement of such other websites, services or applications or their
      content. We have no control over, do not review, and cannot be responsible for these outside
      websites, services or their content. This Privacy Policy does not govern your visits to these
      other websites or services, and your access is entirely at your own risk. These other services
      and applications may set their own cookies on users, collect data, or solicit personal
      information. You should refer to any privacy policies on such services and applications to
      understand how your information may be collected and used, and should choose appropriate
      settings for sharing your personal information before proceeding to any integration with a
      Third Party service or application.
    </p>
    <p>
      <strong>YouTube content.</strong> We access YouTube API Services to show the YouTube Embedded
      Player on websites and applications which may serve advertisements through that interface.
      YouTube receives data about your activity and OpenLikes does not control the data collected by
      YouTube. For more information, view the
      <a href="http://www.google.com/policies/privacy">Google Privacy Policy</a>.
    </p>
    <hr />
    <h5>Additional Information for EU Residents</h5>
    <p>
      In the course of visiting our website, using our mobile applications or browser extensions,
      and/or using our services, we may process the following personal data:
    </p>
    <p>
      <strong>Log Data.</strong> When you interact with the Services our servers automatically
      record information that your browser or device sends. This Log Data may include information
      such as your computer’s IP address, browser type, and operating system, your mobile device
      identifier, the time of day of your access, your general physical location or your more
      specific location if you are connecting through your mobile device, and other analytics that
      help us deliver and monitor the effectiveness of our Services. This is necessary to be enable
      you to use our website and Services. Further, we have a legitimate interest to evaluate the
      effectiveness of our Services. The legal bases for this processing activity are Article 6 sec.
      1 sent. 1 lit. b, f GDPR.
    </p>
    <p>
      <strong>Account Information.</strong> When you create a OpenLikes account, we will collect
      your e-mail address, gender, year of birth and specific or general location. We process this
      information to provide our Services to you. If you personalize your OpenLikes account, we will
      also collect any information you provide, such as your full name, your photo or avatar and any
      optional information you decide to supply about yourself and your interests. The legal basis
      for this processing activity is Article 6 sec. 1 sent. 1 lit. b GDPR ("performance of a
      contract").
    </p>
    <p>
      <strong>Information relating to your use of or interaction with Partner Communities.</strong>
      If you register for our Services via a Partner Community, we will obtain certain Personal
      Information you have provided to the Partner Community (such as your name, profile picture,
      e-mail address, date of birth and specific address or general location). If you enable
      interoperability with a Third Party application, additional Personal Information that you have
      provided to that Third Party application may be collected and/or you may specifically
      authorize OpenLikes to access certain information from such Third Party application. The
      information we obtain may depend on the privacy settings you have with the Partner Community
      or Third Party application and the information obtained may not be visible in your account.
      The legal basis for this processing activity is Article 6 sec. 1 sent. 1 lit. f ("legitimate
      interest") GDPR. We have a legitimate interest to optimize our services.
    </p>
    <p>
      <strong>Profiling.</strong> We also collect some Information about you and your interests as
      you navigate through and interact with our Services such as the URL of page(s) you view and
      the titles and content of the pages you save to OpenLikes. We process this information as part
      of our services to find and recommend new websites that may be of interest to you, and to
      provide a community of individuals with similar tastes and interests as you. The legal basis
      for this processing activity is Article 6 sec. 1 sent. 1 f ("legitimate interest") GDPR. We
      have a legitimate interest to improve our services and provide you the best user experience.
      We expect that this is also in line with your interests.
    </p>
    <p>
      <strong>Postings and Comments.</strong> We process the information you enter into
      postings/comments on our website to monitor, filter and moderate it for the purposes of being
      able to remove profanity and other inappropriate conversations. The legal basis for this
      processing activity is our legitimate interest in being able to defend ourselves from possible
      liability claims that may arise from unlawful comments posted by you, Article 6 sec. 1 sent. 1
      lit. f GDPR ("legitimate interest").
    </p>
    <p>
      <strong>Contacting us.</strong> When you leave us feedback or write to us directly, we will
      collect any information that is contained in your feedback or comment form, which will include
      your e-mail address and other information that you may submit to us for reference or reply.
      This processing activity is based on your (implicit) consent according to Article 6 sec. 1
      sent. 1 lit. a GDPR and/or Article 6 sec. 1 sent. 1 lit. b GDPR ("performance of a contract"),
      depending on the initial purpose for contacting us.
    </p>
    <p>
      We use Technologies such as Cookies, Pixel Tags, Web Beacons, that are essentially small data
      files placed on your computer, tablet, mobile phone, or other devices (referred to
      collectively as a "device") that allow us to record certain pieces of information whenever you
      visit or interact with our Sites, Services, applications, messaging, and tools, and to
      recognize you across devices. These Technologies may be
    </p>
    <ul>
      <li>
        Operationally Necessary. We may use Technologies that are necessary to the operation of our
        Site, Services, applications, and tools. This includes technologies that allow you access to
        our Site, Services, applications, and tools; that are required to identify irregular Site
        behavior, prevent fraudulent activity and improve security; or that allow you to make use of
        our functions such as shopping-carts, saved search, or similar functions. The legal basis is
        Article 6 sec. 1 sent. 1 lit. b GDPR ("performance of a contract").
      </li>
      <li>
        Performance Related. We may use cookies, web beacons, or other similar technologies to
        assess the performance of our Site, Services, applications, and tools, including as part of
        our analytic practices to help us understand how our visitors use our Sites, determine if
        you have interacted with our messaging, determine whether you have viewed an item or link,
        or to improve our Site content, Services, applications, or tools. We have a legitimate
        interest to evaluate the effectiveness of our Services. The legal basis for this processing
        activity is Article 6 sec. 1 sent. 1 lit. f GDPR ("legitimate interest"). These cookies
        include: "branch_session_first", "iterate_hq"
      </li>
      <li>
        Functionality Related. We may use cookies, web beacons, or other similar technologies that
        allow us to offer you enhanced functionality when accessing or using our Site, Services,
        applications, or tools. This may include identifying you when you sign into our Site or
        keeping track of your specified preferences, interests, or past items viewed so that we may
        enhance the presentation of content on our Site. We have a legitimate interest to enhance
        the presentation of content on our Site. The legal basis for this processing activity is
        Article 6 sec. 1 sent. 1 lit. f ("legitimate interest") GDPR. These cookies include:
        "AWSALB", "_csrf", "token", "usr_sess", "user_identified", "reported_url_ids"
      </li>
      <li>
        Advertising or Targeting Related. We may use first-party or third-party cookies and web
        beacons to deliver content, including advertisements relevant to your interests, on our Site
        or on Third Party sites. This includes using technologies to understand the usefulness to
        you of the advertisements and content that has been delivered to you, such as whether you
        have clicked on an advertisement. This processing is based on Art. 6 sec. 1 sent. 1 lit. f
        DSGVO. We have a legitimate interest in analyzing user behavior in order to optimize our
        advertising. These cookies include: "ajs_anonymous_id", "ajs_group_id", "ajs_user_id",
        "_ga", "_gat", "_fbp", "intercom"
      </li>
      <p>
        <strong>Social Media Widgets.</strong> Our Site includes social media features such as the
        Facebook “Like” button and LinkedIn (that might include widgets such as the share this
        button or other interactive mini-programs). These features may collect your IP address,
        which page you are visiting on our Site, and may set a cookie to enable the feature to
        function properly. These social media features are either hosted by a Third Party or hosted
        directly on our Site.
      </p>
      <p>
        <strong>Opt-out.</strong> Most browsers are initially set up to accept cookies and tracking
        pixels. You can reset your browser to refuse all cookies or to indicate when a cookie is
        being set. Instructions to remove persistent cookies can be found through outside resources.
        Mobile devices may contain settings that allow you to disable tracking, analytics and/or
        location data. Be aware, however, that if you choose to disable cookies or opt out of
        tracking or disengage similar features on your mobile device, some areas of the Services may
        not be optimized for your use, you may not be able to access any or all of the functionality
        of the Services, and your experience using the Services will be limited.
      </p>
      <p>
        <strong>Analytics.</strong> We may also use Google Analytics and Google Analytics
        Demographics and Interest Reporting to collect information regarding visitor behavior and
        visitor demographics on some of our Services, and to develop website content. The Provider
        is Google Inc., 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA. We have entered
        into a contract data processing agreement with Google.
      </p>
      <p>
        Google Analytics uses so-called cookies (see above). The information generated by the cookie
        about your use of this website is generally transmitted to a Google server in the United
        States and stored there. The corresponding evaluation of your surfing behavior is made
        available to us in anonymized form.
      </p>
      <p>
        We have activated the IP anonymization function on this website. As a result, your IP
        address is truncated by Google within member states of the European Union or other
        Contracting States to the Agreement on the European Economic Area prior to transmission to
        the United States. Only in exceptional cases the full IP address will be transmitted to a
        Google server in the United States and truncated there.
      </p>
      <p>
        On behalf of the operator of this website, Google will use this information to evaluate your
        use of the website, to compile reports on website activity and to provide other services
        related to website activity and Internet usage to the website operator. The IP address
        transmitted by Google Analytics as part of Google Analytics will not be merged with other
        Google data.
      </p>
      <p>
        In addition, Google is certified under the EU-US Privacy Shield and the Swiss-US Privacy
        Shield, thereby ensuring an appropriate level of data protection at Google in the United
        States.
      </p>
      <p>
        Google Analytics cookies are stored on the basis of Article 6 sec. 1 sent. 1 lit. f
        ("legitimate interest") GDPR. We have a legitimate interest in analyzing user behavior in
        order to optimize both our web website and our advertising.
      </p>
      <p>
        You can prevent the use of Google Analytics at any time, either by installing a Google
        Browser Plugin that will set an opt-out cookie, or through appropriate settings in your
        browser. For more information, visit
        <a href="https://tools.google.com/dlpage/gaoptout?hl=de"
          >https://tools.google.com/dlpage/gaoptout?hl=de</a
        >
        and
        <a href="https://www.google.com/analytics/terms/de.htm"
          >https://www.google.com/analytics/terms/de.htm</a
        >.
      </p>
      <p>
        For more information on the handling of user data at Google Analytics, see the Google
        Privacy Policy:
        <a href="https://support.google.com/analytics/answer/6004245?hl=de"
          >https://support.google.com/analytics/answer/6004245?hl=de</a
        >.
      </p>
      <p>
        This website further uses the "Demographics" feature of Google Analytics. As a result,
        reports can be produced that contain statements on the age, gender and interests of the
        website visitors. This data comes from interest-based advertising of Google as well as
        visitor data from third-party providers. This data cannot be associated with a specific
        person. You can disable this feature at any time through the ad settings in your Google
        Account, or generally prohibit the collection of your data by Google Analytics as described
        above.
      </p>
      <p>
        The legal basis is Article 6 sec. 1 sent. 1 lit. f ("legitimate interest") GDPR. We have a
        legitimate interest in analyzing user behavior in order to optimize both our web website and
        our advertising.
      </p>
      <p>
        <strong>Your rights.</strong> If you are a user based in the European Union, you may be
        entitled to exercise some or all of the following rights:
      </p>
      <ul>
        <li>
          Require (i) information whether your personal data is retained and (ii) access to your
          personal data retained.
        </li>
        <li>
          Request rectification, removal or restriction of your personal data, e.g. because (i) it
          is incomplete or inaccurate, (ii) it is no longer needed for the purposes for which it was
          collected, or (iii) the consent on which the processing was based has been withdrawn.
        </li>
        <li>
          Refuse to provide and – without impact to data processing activities that have taken place
          before such withdrawal – withdraw your consent to processing of your personal data at any
          time.
        </li>
        <li>
          <strong>
            Object, on grounds relating to your particular situation, to processing of your personal
            data,
          </strong>
          <strong>
            in case such processing is based on our or a third party’s legitimate interests or
            object to the
          </strong>
          <strong>use of your personal data for direct marketing at any time.</strong>
        </li>
        <li>
          Take legal actions in relation to any potential breach of your rights regarding the
          processing of your personal data, as well as to lodge complaints before the competent data
          protection regulators.
        </li>
        <li>
          Require (i) to receive the personal data concerning you, which you have provided to us, in
          a structured, commonly used and machine-readable format and (ii) to transmit those data to
          another controller without hindrance from our side. Where technically feasible you shall
          have the right to have the personal data transmitted directly from us to another
          controller.
        </li>
      </ul>
    </ul>
    <br />
  </div>
</template>

<script>
export default {
  props: {},

  data() {
    return {}
  },

  computed: {},

  watch: {},

  methods: {},

  components: {},
}

/* Computed ---------------------------------------------------- */
/* Watch ------------------------------------------------------- */
/* Methods ----------------------------------------------------- */
</script>
